"use client"

import { useAuth } from "@clerk/nextjs"
import { useCallback, useEffect, useRef, useState } from "react"

import {
  deleteTimelineSave,
  fetchTimelineSaves,
  postTimelineSave,
} from "@/lib/timeline-api"
import type { TimelineSave, RsvpStatus } from "@/lib/timeline-store"
import {
  clearLegacyTimelineStorage,
  getRsvpStatus,
  markTimelineMigrationDone,
  migrateRsvpFromLegacySaves,
  migrationAlreadyRan,
  readLegacyTimelineSavesRaw,
  updateRsvp as updateRsvpStore,
} from "@/lib/timeline-store"

function savesFromWire(
  rows: { eventId: string; createdAt: string }[],
): TimelineSave[] {
  return rows.map((r) => ({
    eventId: r.eventId,
    savedAt: new Date(r.createdAt),
    rsvpStatus: null,
    rsvpUpdatedAt: null,
  }))
}

export function useTimeline() {
  const { userId, isLoaded: authLoaded } = useAuth()
  const [saves, setSaves] = useState<TimelineSave[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const savesRef = useRef<TimelineSave[]>([])
  const pendingAdd = useRef<Set<string>>(new Set())
  const pendingRemove = useRef<Set<string>>(new Set())

  useEffect(() => {
    savesRef.current = saves
  }, [saves])

  const loadFromServer = useCallback(async () => {
    const rows = await fetchTimelineSaves()
    setSaves(savesFromWire(rows))
  }, [])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      if (!authLoaded) return

      if (!userId?.trim()) {
        if (!cancelled) {
          setSaves([])
          setIsLoaded(true)
        }
        return
      }

      // Until this finishes, timeline state is for the previous user (e.g. signed-out empty list).
      // Reset loaded so callers (e.g. post–sign-in auto-save) wait for a fresh fetch for this user.
      if (!cancelled) setIsLoaded(false)

      try {
        if (!migrationAlreadyRan()) {
          const legacy = readLegacyTimelineSavesRaw()
          if (legacy.length > 0) {
            migrateRsvpFromLegacySaves(legacy)
            for (const s of legacy) {
              if (!s.eventId.trim()) continue
              try {
                await postTimelineSave(s.eventId)
              } catch {
                /* best-effort migration */
              }
            }
            clearLegacyTimelineStorage()
          }
          markTimelineMigrationDone()
        }

        if (!cancelled) await loadFromServer()
      } catch {
        if (!cancelled) setSaves([])
      } finally {
        if (!cancelled) setIsLoaded(true)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [authLoaded, userId, loadFromServer])

  const refresh = useCallback(async () => {
    if (!userId?.trim()) {
      setSaves([])
      return
    }
    try {
      await loadFromServer()
    } catch {
      /* keep cache */
    }
  }, [userId, loadFromServer])

  const saveToTimeline = useCallback(
    async (eventId: string): Promise<boolean> => {
      const id = eventId.trim()
      if (!userId?.trim() || !id) return false
      if (savesRef.current.some((s) => s.eventId === id)) return true
      if (pendingAdd.current.has(id)) return false

      pendingAdd.current.add(id)
      const prev = savesRef.current
      const optimistic: TimelineSave = {
        eventId: id,
        savedAt: new Date(),
        rsvpStatus: null,
        rsvpUpdatedAt: null,
      }
      setSaves((p) => [optimistic, ...p.filter((s) => s.eventId !== id)])

      try {
        await postTimelineSave(id)
        pendingAdd.current.delete(id)
        return true
      } catch {
        pendingAdd.current.delete(id)
        setSaves(prev)
        return false
      }
    },
    [userId],
  )

  const removeFromTimeline = useCallback(
    async (eventId: string): Promise<boolean> => {
      const id = eventId.trim()
      if (!userId?.trim() || !id) return false
      if (!savesRef.current.some((s) => s.eventId === id)) return false
      if (pendingRemove.current.has(id)) return false

      pendingRemove.current.add(id)
      const prev = savesRef.current
      setSaves((p) => p.filter((s) => s.eventId !== id))

      try {
        const removed = await deleteTimelineSave(id)
        pendingRemove.current.delete(id)
        if (!removed) {
          setSaves(prev)
          return false
        }
        return true
      } catch {
        pendingRemove.current.delete(id)
        setSaves(prev)
        return false
      }
    },
    [userId],
  )

  const updateRsvp = useCallback((eventId: string, status: RsvpStatus) => {
    const ok = updateRsvpStore(eventId, status)
    return ok
  }, [])

  const isSaved = useCallback((eventId: string) => {
    return saves.some((s) => s.eventId === eventId)
  }, [saves])

  const getRsvp = useCallback((eventId: string): RsvpStatus => {
    return getRsvpStatus(eventId)
  }, [])

  return {
    saves,
    isLoaded,
    saveToTimeline,
    removeFromTimeline,
    updateRsvp,
    isSaved,
    getRsvp,
    refresh,
  }
}
